// src/components/booking/RescheduleFlow.js
// Two-step reschedule flow: 1) Select new time, 2) Review & confirm
import React, { useState, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const REGION = 'us-west1';

const REASON_OPTIONS = [
  { value: '', label: 'Why are you rescheduling? (optional)' },
  { value: 'schedule_conflict', label: 'Schedule conflict' },
  { value: 'personal', label: 'Something came up' },
  { value: 'different_time', label: 'Need a different time/day' },
  { value: 'other', label: 'Other' },
];

// ── Time helpers (matching BookingPage.js logic) ──

const parseTime = (str) => {
  if (!str) return 0;
  const [h, m] = str.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

const formatTime = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayH}:${m.toString().padStart(2, '0')} ${ampm}`;
};

const formatDateDisplay = (date) => {
  return date.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
};

const formatDateTime = (dateTime, timezone) => {
  try {
    const date = dateTime?._seconds ? new Date(dateTime._seconds * 1000) : new Date(dateTime);
    return date.toLocaleString('en-US', {
      timeZone: timezone || 'UTC',
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch { return String(dateTime); }
};

const getTimezoneOffset = (tz, date) => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, timeZoneName: 'longOffset',
    });
    const parts = formatter.formatToParts(date);
    const offset = parts.find(p => p.type === 'timeZoneName')?.value || '';
    const match = offset.match(/GMT([+-]\d{1,2}(?::\d{2})?)/);
    if (match) {
      let val = match[1];
      if (!val.includes(':')) val += ':00';
      if (val.length === 5) val = val.replace(/^([+-])(\d):/, '$10$2:');
      return val;
    }
    return '+00:00';
  } catch { return '+00:00'; }
};

const defaultAvailability = {
  schedule: {
    monday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
    tuesday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
    wednesday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
    thursday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
    friday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
    saturday: { enabled: false, blocks: [] },
    sunday: { enabled: false, blocks: [] },
  },
  bufferTime: 15, bookingWindow: 30, minNotice: 24, timeOff: [],
};

// ── Component ──

const RescheduleFlow = ({ booking, bookingId, token, onRescheduled, onBack }) => {
  const [step, setStep] = useState(1); // 1 = select time, 2 = review
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedService, setSelectedService] = useState(null); // null = keep current
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const availability = booking.availability || defaultAvailability;
  const effectiveAvailability = availability?.schedule && Object.keys(availability.schedule).length > 0
    ? availability : defaultAvailability;
  const timezone = availability?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const services = booking.services || [];
  const currentService = booking.service;
  const activeService = selectedService || currentService;

  // Generate time slots for a given date
  const generateTimeSlots = (date) => {
    if (!date) return [];
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const daySchedule = effectiveAvailability?.schedule?.[dayNames[date.getDay()]];
    if (!daySchedule?.enabled || !daySchedule?.blocks?.length) return [];

    const slots = [];
    const serviceDuration = Number(activeService?.duration) || 60;
    const buffer = Number(effectiveAvailability?.bufferTime) || 15;
    const minNoticeHours = Number(effectiveAvailability?.minNotice) || 24;
    const minStartTime = new Date(Date.now() + minNoticeHours * 3600000);

    daySchedule.blocks.forEach((block) => {
      let current = parseTime(block.start);
      const end = parseTime(block.end);
      while (current + serviceDuration <= end) {
        const slotDate = new Date(date);
        slotDate.setHours(Math.floor(current / 60), current % 60, 0, 0);
        if (slotDate >= minStartTime) {
          slots.push({ time: current, label: formatTime(current) });
        }
        current += serviceDuration + buffer;
      }
    });
    return slots;
  };

  const timeSlots = useMemo(() => generateTimeSlots(selectedDate), [selectedDate, activeService?.duration]);

  // Calendar helpers
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const isDateAvailable = (date) => {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const daySchedule = effectiveAvailability?.schedule?.[dayNames[date.getDay()]];
    if (!daySchedule?.enabled) return false;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (date < today) return false;
    const windowDays = Number(effectiveAvailability?.bookingWindow) || 30;
    const maxDate = new Date(today.getTime() + windowDays * 86400000);
    if (date > maxDate) return false;
    return true;
  };

  const isSameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  // Price calculation
  const priceDifference = selectedService
    ? Math.round(((selectedService.price || 0) - (currentService?.price || 0)) * 100)
    : 0;

  // Build ISO datetime for the CF
  const buildISODateTime = () => {
    if (!selectedDate || selectedTime === null) return null;
    const hours = Math.floor(selectedTime / 60);
    const mins = selectedTime % 60;
    const timeStr = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`;
    const tzDateStr = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(selectedDate);
    const offset = getTimezoneOffset(timezone, selectedDate);
    return `${tzDateStr}T${timeStr}${offset}`;
  };

  const handleConfirm = async () => {
    setProcessing(true);
    setError(null);
    try {
      const functions = getFunctions(undefined, REGION);
      const rescheduleBookingFn = httpsCallable(functions, 'rescheduleBooking');
      const result = await rescheduleBookingFn({
        bookingId,
        token,
        newDateTime: buildISODateTime(),
        newTimezone: timezone,
        newServiceId: selectedService ? (selectedService.id || selectedService.name) : null,
        reason: reason ? { category: reason, details: details.trim() || null } : null,
      });
      onRescheduled(result.data);
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setProcessing(false);
    }
  };

  // ── Step 2: Review ──
  if (step === 2) {
    const newFormatted = selectedDate && selectedTime !== null
      ? `${formatDateDisplay(selectedDate)} at ${formatTime(selectedTime)}`
      : '';
    const oldFormatted = formatDateTime(booking.dateTime, booking.timezone);
    const wasFree = (booking.rescheduleCount || 0) === 0;

    return (
      <div style={{ animation: 'bmFadeIn 0.3s ease' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#111827', marginBottom: '24px' }}>
          Review your changes
        </h2>

        {/* Side-by-side cards */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
          {/* Current */}
          <div style={{ flex: 1, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '16px' }}>
            <p style={{ fontSize: '11px', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Current</p>
            <p style={{ fontSize: '14px', fontWeight: 600, color: '#6B7280', marginBottom: '4px', textDecoration: 'line-through' }}>{currentService?.name}</p>
            <p style={{ fontSize: '13px', color: '#9CA3AF', textDecoration: 'line-through' }}>{oldFormatted}</p>
            {(currentService?.price || 0) > 0 && (
              <p style={{ fontSize: '13px', color: '#9CA3AF', marginTop: '4px' }}>${currentService.price.toFixed(2)}</p>
            )}
          </div>
          {/* New */}
          <div style={{ flex: 1, background: '#fff', border: '1px solid #A7F3D0', borderLeft: '3px solid #bb9749', borderRadius: '10px', padding: '16px' }}>
            <p style={{ fontSize: '11px', fontWeight: 600, color: '#bb9749', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>New</p>
            <p style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>{activeService?.name}</p>
            <p style={{ fontSize: '13px', color: '#374151' }}>{newFormatted}</p>
            {(activeService?.price || 0) > 0 && (
              <p style={{ fontSize: '13px', color: '#374151', marginTop: '4px' }}>${activeService.price.toFixed(2)}</p>
            )}
          </div>
        </div>

        {/* Price/reschedule note */}
        <div style={{
          padding: '12px 16px', borderRadius: '8px', marginBottom: '24px',
          background: priceDifference > 0 ? '#FFFBEB' : '#ECFDF5',
          border: priceDifference > 0 ? '1px solid #FDE68A' : '1px solid #A7F3D0',
        }}>
          {priceDifference === 0 && wasFree && (
            <p style={{ fontSize: '14px', color: '#065F46', margin: 0 }}>✓ Free reschedule (1 of 1 used)</p>
          )}
          {priceDifference === 0 && !wasFree && (
            <p style={{ fontSize: '14px', color: '#065F46', margin: 0 }}>✓ No additional charges</p>
          )}
          {priceDifference > 0 && (
            <p style={{ fontSize: '14px', color: '#92400E', margin: 0 }}>
              Additional charge: ${(priceDifference / 100).toFixed(2)}
            </p>
          )}
          {priceDifference < 0 && (
            <p style={{ fontSize: '14px', color: '#065F46', margin: 0 }}>
              Refund: ${(Math.abs(priceDifference) / 100).toFixed(2)} — will appear within 5–10 business days
            </p>
          )}
        </div>

        {error && (
          <div style={{ padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', marginBottom: '16px' }}>
            <p style={{ fontSize: '14px', color: '#991B1B', margin: 0 }}>{error}</p>
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px' }}>
          <span role="button" tabIndex={0} onClick={() => !processing && setStep(1)} onKeyDown={(e) => e.key === 'Enter' && !processing && setStep(1)} style={{ ...styles.secondaryBtn, userSelect: 'none' }}>Go back</span>
          <button onClick={handleConfirm} disabled={processing} style={{ ...styles.goldBtn, opacity: processing ? 0.7 : 1 }}>
            {processing ? 'Rescheduling...' : 'Confirm reschedule'}
          </button>
        </div>
      </div>
    );
  }

  // ── Step 1: Select new time ──
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const monthLabel = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div style={{ animation: 'bmFadeIn 0.3s ease' }}>
      <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
        Reschedule your session
      </h2>
      <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>
        Select a new date and time for your {currentService?.name || 'session'}.
      </p>

      {(booking.rescheduleCount || 0) >= 1 && (
        <div style={{ padding: '10px 14px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '8px', marginBottom: '16px' }}>
          <p style={{ fontSize: '13px', color: '#92400E', margin: 0 }}>
            Your practitioner's cancellation policy may apply to this reschedule.
          </p>
        </div>
      )}

      {/* Service selector (only if multiple services) */}
      {services.length > 1 && (
        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '6px', display: 'block' }}>Service</label>
          <select
            value={selectedService ? (selectedService.id || selectedService.name) : ''}
            onChange={(e) => {
              if (!e.target.value) { setSelectedService(null); setSelectedTime(null); }
              else {
                const s = services.find(s => (s.id || s.name) === e.target.value);
                setSelectedService(s || null);
                setSelectedTime(null);
              }
            }}
            style={styles.select}
          >
            <option value="">Keep current — {currentService?.name} (${(currentService?.price || 0).toFixed(2)})</option>
            {services.filter(s => (s.id || s.name) !== (currentService?.id || currentService?.name)).map(s => (
              <option key={s.id || s.name} value={s.id || s.name}>
                {s.name} — ${(s.price || 0).toFixed(2)} ({s.duration || 60} min)
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Calendar */}
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <button onClick={() => setCurrentMonth(new Date(year, month - 1, 1))} style={styles.calNavBtn}><ChevronLeft size={18} /></button>
          <span style={{ fontSize: '15px', fontWeight: 600, color: '#111827' }}>{monthLabel}</span>
          <button onClick={() => setCurrentMonth(new Date(year, month + 1, 1))} style={styles.calNavBtn}><ChevronRight size={18} /></button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', textAlign: 'center' }}>
          {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
            <div key={d} style={{ fontSize: '11px', fontWeight: 600, color: '#9CA3AF', padding: '4px' }}>{d}</div>
          ))}
          {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const date = new Date(year, month, day);
            const available = isDateAvailable(date);
            const selected = isSameDay(date, selectedDate);
            return (
              <button
                key={day}
                disabled={!available}
                onClick={() => { setSelectedDate(date); setSelectedTime(null); }}
                style={{
                  padding: '8px 4px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: 500,
                  cursor: available ? 'pointer' : 'default',
                  background: selected ? '#bb9749' : available ? '#fff' : 'transparent',
                  color: selected ? '#fff' : available ? '#111827' : '#D1D5DB',
                  transition: 'all 0.15s ease',
                }}
              >{day}</button>
            );
          })}
        </div>
      </div>

      {/* Time slots */}
      {selectedDate && (
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>
            Available times for {formatDateDisplay(selectedDate)}
          </p>
          {timeSlots.length === 0 ? (
            <p style={{ fontSize: '13px', color: '#9CA3AF' }}>No available times on this date.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
              {timeSlots.map(slot => (
                <button
                  key={slot.time}
                  onClick={() => setSelectedTime(slot.time)}
                  style={{
                    padding: '10px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                    border: selectedTime === slot.time ? '2px solid #bb9749' : '1px solid #E5E7EB',
                    background: selectedTime === slot.time ? '#FEF3C7' : '#fff',
                    color: '#111827', cursor: 'pointer', transition: 'all 0.15s ease',
                  }}
                >{slot.label}</button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Reason */}
      <div style={{ marginBottom: '16px' }}>
        <select value={reason} onChange={(e) => setReason(e.target.value)} style={styles.select}>
          {REASON_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      </div>

      {reason && (
        <div style={{ marginBottom: '20px', position: 'relative' }}>
          <textarea
            value={details}
            onChange={(e) => setDetails(e.target.value.slice(0, 300))}
            placeholder="Anything else? (optional)"
            rows={2}
            style={{ ...styles.select, resize: 'vertical', height: 'auto', paddingBottom: '24px' }}
          />
          <span style={{ position: 'absolute', bottom: '8px', right: '12px', fontSize: '11px', color: details.length >= 280 ? '#DC2626' : '#9CA3AF' }}>
            {details.length}/300
          </span>
        </div>
      )}

      {/* Buttons */}
      <div style={{ display: 'flex', gap: '12px' }}>
        <span role="button" tabIndex={0} onClick={onBack} onKeyDown={(e) => e.key === 'Enter' && onBack()} style={{ ...styles.secondaryBtn, userSelect: 'none' }}>Go back</span>
        <button
          disabled={!selectedDate || selectedTime === null}
          onClick={() => setStep(2)}
          style={{
            ...styles.goldBtn,
            opacity: (!selectedDate || selectedTime === null) ? 0.4 : 1,
            cursor: (!selectedDate || selectedTime === null) ? 'not-allowed' : 'pointer',
          }}
        >Continue</button>
      </div>
    </div>
  );
};

const styles = {
  goldBtn: {
    flex: 1, padding: '14px 24px', background: '#bb9749', color: '#fff',
    border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600,
    cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", transition: 'opacity 0.2s ease',
  },
  secondaryBtn: {
    flex: 1, padding: '14px 24px', background: 'transparent', color: '#374151',
    border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '15px', fontWeight: 500,
    cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
  },
  select: {
    width: '100%', padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: '8px',
    fontSize: '14px', color: '#374151', background: '#fff',
    fontFamily: "'Montserrat', sans-serif", outline: 'none', boxSizing: 'border-box',
  },
  calNavBtn: {
    background: 'none', border: '1px solid #E5E7EB', borderRadius: '8px',
    padding: '6px', cursor: 'pointer', color: '#374151', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
  },
};

export default RescheduleFlow;
